import { useState, useEffect } from 'react';
import { getAdminOrders, updateOrderStatus } from '@/lib/api';
import { Package, Clock, Truck, CheckCircle, FileText, ChevronDown } from 'lucide-react';

const STATUSES = [
  { value: 'placed', label: 'Commande passée', color: 'bg-blue-100 text-blue-700' },
  { value: 'sent_to_supplier', label: 'Envoyée fournisseur', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'delivery_confirmed', label: 'Date confirmée', color: 'bg-orange-100 text-orange-700' },
  { value: 'in_transit', label: 'En transit', color: 'bg-purple-100 text-purple-700' },
  { value: 'delivered', label: 'Livrée', color: 'bg-green-100 text-green-700' },
  { value: 'invoiced', label: 'Facturée', color: 'bg-gray-100 text-gray-700' },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetch = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    getAdminOrders(params).then(r => { setOrders(r.data.orders); setTotal(r.data.total); setLoading(false); });
  };

  useEffect(() => { fetch(); }, [statusFilter]);

  const handleStatusChange = async (orderId, newStatus) => {
    await updateOrderStatus(orderId, { status: newStatus });
    fetch();
  };

  return (
    <div data-testid="admin-orders" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold text-[#111] tracking-tight">Commandes ({total})</h1>
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setStatusFilter('')} className={`px-3 py-1.5 text-xs font-semibold border rounded-sm ${!statusFilter ? 'bg-[#111] text-white border-[#111]' : 'border-black/15'}`}>Toutes</button>
        {STATUSES.map(s => (
          <button key={s.value} onClick={() => setStatusFilter(s.value)} className={`px-3 py-1.5 text-xs font-semibold border rounded-sm ${statusFilter === s.value ? 'bg-[#111] text-white border-[#111]' : 'border-black/15'}`}>{s.label}</button>
        ))}
      </div>

      {/* Orders */}
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_,i) => <div key={i} className="h-16 bg-[#F8F9FA] animate-pulse border border-black/10" />)}</div>
      ) : orders.length === 0 ? (
        <div className="p-12 text-center border border-black/10"><Package size={36} className="mx-auto text-black/10 mb-3" /><p className="text-sm text-black/40">Aucune commande</p></div>
      ) : (
        <div className="border border-black/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50 border-b border-black/10">
                <th className="text-left px-4 py-2 font-semibold">N° Commande</th>
                <th className="text-left px-4 py-2 font-semibold">Client</th>
                <th className="text-center px-4 py-2 font-semibold">Articles</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
                <th className="text-center px-4 py-2 font-semibold">Statut</th>
                <th className="text-right px-4 py-2 font-semibold">Date</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const st = STATUSES.find(s => s.value === o.status) || STATUSES[0];
                const isExpanded = expandedOrder === o.id;
                return (
                  <>
                    <tr key={o.id} className="border-t border-black/5 hover:bg-[#F8F9FA] cursor-pointer transition-colors duration-200" onClick={() => setExpandedOrder(isExpanded ? null : o.id)}>
                      <td className="px-4 py-3 font-medium">{o.order_number}</td>
                      <td className="px-4 py-3 text-black/60">{o.company_name}</td>
                      <td className="px-4 py-3 text-center text-black/50">{o.items?.length || 0}</td>
                      <td className="px-4 py-3 text-right font-bold">{o.total?.toFixed(2)} EUR</td>
                      <td className="px-4 py-3 text-center">
                        <select
                          value={o.status}
                          onChange={(e) => { e.stopPropagation(); handleStatusChange(o.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          className={`text-xs font-semibold px-2 py-1 rounded-sm border-0 cursor-pointer ${st.color}`}
                        >
                          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-black/40">{new Date(o.created_at).toLocaleDateString('fr-BE')}</td>
                      <td className="px-2 py-3"><ChevronDown size={14} className={`text-black/30 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} /></td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${o.id}-detail`}>
                        <td colSpan={7} className="px-4 py-4 bg-[#F8F9FA] border-t border-black/5">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-xs font-bold uppercase tracking-wider text-black/40 mb-2">Articles</h4>
                              <div className="space-y-1">
                                {o.items?.map((item, idx) => (
                                  <div key={idx} className="flex justify-between text-xs">
                                    <span>{item.name} x{item.quantity}</span>
                                    <span className="font-medium">{item.line_total?.toFixed(2)} EUR</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-bold uppercase tracking-wider text-black/40 mb-2">Détails</h4>
                              <div className="text-xs space-y-1">
                                <p>Acompte: <strong>{o.deposit?.toFixed(2)} EUR</strong></p>
                                <p>Solde: <strong>{o.balance?.toFixed(2)} EUR</strong></p>
                                {o.estimated_delivery_date && <p>Livraison: <strong>{o.estimated_delivery_date}</strong></p>}
                                {o.delivery_notes && <p>Notes: {o.delivery_notes}</p>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
